/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { TaskExecutionController } from '@karya/generic-task-react';
import { ChatMessage } from '../chakra/shared/ChatMessage';
import { PlatformComponentRenderer } from './PlatformComponentRenderer';
import { UserComponentRenderer } from './UserComponentRenderer';
import StartComponentRenderer from './StartComponentRenderer';
import ErrorDisplay from '../../../components/Error';

interface TaskComponentItemProps {
  node: TaskExecutionController['history'][0];
  index: number;
  controller: TaskExecutionController;
  isActiveAssignment?: boolean;
}

/**
 * Renders a single component node based on its type
 */
export const TaskComponentItem: React.FC<TaskComponentItemProps> = ({
  node,
  index,
  controller,
  isActiveAssignment,
}) => {
  const isCurrent = index === controller.currentIndex && !!isActiveAssignment;
  const ctype = node.component.ctype;

  // Handle END component
  if (ctype === 'END') {
    return null;
  }

  if (ctype === 'START') {
    return <StartComponentRenderer node={node} controller={controller} isCurrent={isCurrent} />;
  }

  // Route by ctype
  if (ctype === 'PLATFORM') {
    return (
      <ChatMessage isPlatform={true}>
        <PlatformComponentRenderer node={node} controller={controller} isCurrent={isCurrent} />
      </ChatMessage>
    );
  }

  if (ctype === 'USER') {
    return (
      <ChatMessage isPlatform={false}>
        <UserComponentRenderer node={node} controller={controller} isCurrent={isCurrent} />
      </ChatMessage>
    );
  }

  // Fallback for unknown ctypes
  return (
    <ErrorDisplay
      message={`Unknown component type: ${ctype}`}
      debugMessage={`At component with key ${node.component.key}`}
      navigateButton={{
        to: '/dashboard',
        label: 'Go to Dashboard',
      }}
    />
  );
};
